import { ChangeEvent } from "react";
import {
  Icon,
  Text,
  Box,
  useRadio,
  useRadioGroup,
  TextProps,
  RadioProps,
  RadioGroupProps,
  Grid,
  GridProps,
  GridItem
} from "@chakra-ui/react";

import VeryHappy from "./icons/very-happy";
import Happy from "./icons/happy";
import Neutral from "./icons/neutral";
import UnHappy from "./icons/unhappy";
import VeryUnHappy from "./icons/very-unhappy";

interface EmojiRadioProps {
  isDisabled: boolean;
  handleChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  value: string;
  label: string;
  emojiType: "VeryUnHappy" | "UnHappy" | "Neutral" | "Happy" | "VeryHappy";
  labelProps?: TextProps;
  radioProps?: RadioProps;
}

const getEmoji = {
  VeryUnHappy,
  UnHappy,
  Neutral,
  Happy,
  VeryHappy
};

const EmojiRadio = (props: EmojiRadioProps) => {
  const {
    handleChange,
    value,
    isDisabled,
    label,
    emojiType,
    labelProps,
    radioProps
  } = props;

  const { getInputProps, getCheckboxProps } = useRadio({
    value,
    isDisabled: isDisabled,
    onChange: handleChange,
    ...radioProps
  });

  return (
    <Box as="label">
      <input {...getInputProps()} title={label} />
      <Box
        {...getCheckboxProps()}
        px={4}
        height="auto"
        pb={{ lg: 4 }}
        cursor="pointer"
        display="flex"
        textAlign="center"
        alignItems="center"
        justifyContent="center"
        flexDirection={{ lg: "column" }}
        sx={{
          background: "white",
          borderLeftWidth: { lg: 0, sm: "4px" },
          borderLeftStyle: { lg: "initial", sm: "solid" },
          borderLeftColor: "transparent",

          borderBottomWidth: { lg: "4px", sm: 0 },
          borderBottomStyle: { lg: "solid", sm: "initial" },
          borderBottomColor: "transparent",

          svg: {
            boxSize: 12,
            color: "gray.200"
          },

          p: {
            pt: { lg: 3 },
            width: "100%",
            whiteSpace: "normal",
            pl: { base: 3, lg: 0 },
            wordBreak: "break-word",
            display: { xs: "none", sm: "block" }
          },

          _hover: {
            background: "white",
            "@media (hover: hover) and (pointer: fine)": {
              svg: {
                color: "yellow.200"
              },
              p: {
                fontWeight: "600"
              }
            }
          },

          _focus: {
            boxShadow: "outline",
            background: "white",

            borderTopLeftRadius: { lg: "md", sm: 0 },
            borderTopRightRadius: { sm: "md" },
            borderBottomLeftRadius: { sm: 0 },
            borderBottomRightRadius: { lg: 0, sm: "md" },

            svg: {
              color: "yellow.200"
            },
            p: {
              fontWeight: "600"
            }
          },

          _checked: {
            background: "white",

            borderLeftColor: { sm: "brand.500", lg: "transparent" },
            borderBottomColor: {
              lg: "brand.500",
              sm: "transparent"
            },

            svg: {
              color: "yellow.300"
            },
            p: {
              fontWeight: "700"
            }
          },

          _disabled: {
            opacity: 0.4,
            boxShadow: "none",
            cursor: "not-allowed",
            userSelect: "none",

            _hover: {
              svg: {
                color: "gray.200"
              },
              p: {
                fontWeight: "normal"
              }
            },

            _checked: {
              svg: {
                color: "yellow.300"
              },
              p: {
                fontWeight: "700"
              }
            }
          }
        }}
      >
        <Icon as={getEmoji[emojiType]} />
        {label && <Text {...labelProps}>{label}</Text>}
      </Box>
    </Box>
  );
};

interface EmojiRadioGroupProps {
  id?: string;
  isDisabled: boolean;
  handleChange: (value: string) => void;
  value: string;
  emojiGroup: {
    type: "VeryUnHappy" | "UnHappy" | "Neutral" | "Happy" | "VeryHappy";
    id: string;
    note: string;
  }[];
  groupProps?: RadioGroupProps;
  groupLayoutProps?: GridProps;
  labelProps?: TextProps;
  radioProps?: RadioProps;
  spacing?: number;
}

const EmojiRadioGroup = (props: EmojiRadioGroupProps) => {
  const {
    id,
    handleChange,
    value,
    isDisabled,
    emojiGroup,
    groupProps,
    groupLayoutProps,
    labelProps,
    radioProps,
    spacing
  } = props;

  const { getRootProps, getRadioProps } = useRadioGroup({
    value,
    onChange: handleChange
  });

  const defaultGroupProps = {
    ...getRootProps(),
    ...groupProps
  };

  return (
    <Box {...(defaultGroupProps as any)} id={id}>
      <Grid
        w="100%"
        templateColumns={{ lg: `repeat(${emojiGroup.length}, 1fr)` }}
        gap={spacing}
        {...groupLayoutProps}
      >
        {emojiGroup.map(({ type, id, note }, index) => {
          return (
            <GridItem
              key={`emoji-option-${id}`}
              display="flex"
              justifyContent={{ lg: "center" }}
            >
              <EmojiRadio
                key={index}
                value={type}
                isDisabled={isDisabled}
                label={note}
                emojiType={type}
                labelProps={labelProps}
                radioProps={getRadioProps({
                  value: id,
                  isDisabled: isDisabled,
                  ...radioProps
                })}
              />
            </GridItem>
          );
        })}
      </Grid>
    </Box>
  );
};

export { EmojiRadio, EmojiRadioGroup, type EmojiRadioProps };
